import type { Equipment, Pipeline } from '@/types';

// 港机码头运维 3D 场景配置 v1
// 10 个关键设备 + 4 段主干连接
// 说明：本场景为 3D 渲染，x/y/width/height 仅用于复用 2D 数据结构，
// 真实 3D 空间位置在 src/scenes/portcrane/PortCrane3D.tsx 内单独映射。

// 时间常数（秒）—— 港机是重载大惯性机械系统
const TAU_BOOM = 8;       // 大梁俯仰：大梁质量大、俯仰油缸响应慢
const TAU_HOIST = 2;      // 起升速度：变频电机响应较快
const TAU_TROLLEY = 2;    // 小车速度：响应快
const TAU_GANTRY = 5;     // 大车走行：整机惯性中等
const TAU_LOAD = 1;       // 载荷类：钢丝绳张力响应快
const TAU_WIND = 1;       // 风速：气象变化快
const TAU_POWER = 1;      // 电气参数：响应快
const TAU_RAIL = 20;      // 轨道磨损：极慢累积
const TAU_PLC = 1;        // PLC：响应快

export const portcraneEquipments: Equipment[] = [
  // ============= 1. 大梁 / 臂架 — 俯仰与载荷 =============
  {
    id: 'BOOM', name: '大梁', type: 'main-drive',
    x: 300, y: 100, width: 400, height: 40,
    status: 'normal', template: 'portcrane',
    parameters: [
      { id: 'BOOM-ANGLE', name: '俯仰角', value: 0, unit: '°', min: 0, max: 90, normalMin: 0, normalMax: 5, trend: [], tau: TAU_BOOM },
      { id: 'BOOM-LOAD', name: '大梁载荷', value: 20, unit: 't', min: 0, max: 100, normalMin: 0, normalMax: 60, trend: [], tau: TAU_LOAD },
    ],
  },

  // ============= 2. 起升机构 — 速度 / 载荷 / 高度 =============
  {
    id: 'HOIST', name: '起升机构', type: 'compressor',
    x: 450, y: 180, width: 100, height: 80,
    status: 'normal', template: 'portcrane',
    parameters: [
      { id: 'HOIST-SPD', name: '起升速度', value: 45, unit: 'm/min', min: 0, max: 120, normalMin: 30, normalMax: 60, trend: [], tau: TAU_HOIST },
      { id: 'HOIST-LOAD', name: '起升载荷', value: 40, unit: 't', min: 0, max: 100, normalMin: 0, normalMax: 65, trend: [], tau: TAU_LOAD },
      { id: 'HOIST-POS', name: '起升高度', value: 20, unit: 'm', min: 0, max: 50, normalMin: 0, normalMax: 50, trend: [], tau: TAU_HOIST },
    ],
  },

  // ============= 3. 小车 — 位置 / 速度 / 跑偏 =============
  {
    id: 'TROLLEY', name: '小车', type: 'robot',
    x: 500, y: 200, width: 80, height: 60,
    status: 'normal', template: 'portcrane',
    parameters: [
      { id: 'TROLLEY-POS', name: '小车位置', value: 30, unit: 'm', min: 0, max: 60, normalMin: 0, normalMax: 60, trend: [], tau: TAU_TROLLEY },
      { id: 'TROLLEY-SPD', name: '小车速度', value: 40, unit: 'm/min', min: 0, max: 120, normalMin: 20, normalMax: 60, trend: [], tau: TAU_TROLLEY },
      { id: 'TROLLEY-DEV', name: '小车跑偏量', value: 5, unit: 'mm', min: 0, max: 50, normalMin: 0, normalMax: 20, trend: [], tau: TAU_TROLLEY },
    ],
  },

  // ============= 4. 吊具 — 状态 / 扭角 / 载荷 =============
  {
    id: 'SPREADER', name: '吊具', type: 'fixture',
    x: 520, y: 280, width: 60, height: 80,
    status: 'normal', template: 'portcrane',
    parameters: [
      { id: 'SPREADER-STATUS', name: '吊具状态', value: 1, unit: '', min: 0, max: 2, normalMin: 0, normalMax: 2, trend: [], tau: 1 },
      { id: 'SPREADER-TWIST', name: '吊具扭角', value: 0.5, unit: '°', min: 0, max: 10, normalMin: 0, normalMax: 2, trend: [], tau: TAU_LOAD },
      { id: 'SPREADER-LOAD', name: '吊具载荷', value: 40, unit: 't', min: 0, max: 100, normalMin: 0, normalMax: 65, trend: [], tau: TAU_LOAD },
      // 摇摆角：吊具在运行过程中的摆动角度
      { id: 'SWAY-ANG', name: '摇摆角', value: 0, unit: '°', min: 0, max: 20, normalMin: 0, normalMax: 5, trend: [], tau: 1 },
    ],
  },

  // ============= 5. 大车走行 — 速度 / 位置 / 制动 =============
  {
    id: 'GANTRY', name: '大车走行', type: 'crane-iso',
    x: 200, y: 400, width: 160, height: 60,
    status: 'normal', template: 'portcrane',
    parameters: [
      { id: 'GANTRY-SPD', name: '大车速度', value: 15, unit: 'm/min', min: 0, max: 60, normalMin: 0, normalMax: 30, trend: [], tau: TAU_GANTRY },
      { id: 'GANTRY-POS', name: '大车位置', value: 100, unit: 'm', min: 0, max: 500, normalMin: 0, normalMax: 500, trend: [], tau: TAU_GANTRY },
      { id: 'GANTRY-BRAKE', name: '制动状态', value: 0, unit: '', min: 0, max: 1, normalMin: 0, normalMax: 1, trend: [], tau: 1 },
    ],
  },

  // ============= 6. PLC 控制柜 — CPU / 通讯 / 报警 =============
  {
    id: 'PLC', name: 'PLC控制柜', type: 'control_box',
    x: 150, y: 300, width: 80, height: 100,
    status: 'normal', template: 'portcrane',
    parameters: [
      { id: 'PLC-CPU', name: 'CPU负载', value: 35, unit: '%', min: 0, max: 100, normalMin: 0, normalMax: 60, trend: [], tau: TAU_PLC },
      { id: 'PLC-COMM', name: '通讯状态', value: 0, unit: '', min: 0, max: 1, normalMin: 0, normalMax: 0, trend: [], tau: 1 },
      { id: 'PLC-ALARM', name: '报警数', value: 0, unit: '个', min: 0, max: 50, normalMin: 0, normalMax: 5, trend: [], tau: TAU_PLC },
      // 通信状态：PLC 与上位机通信链路状态（1=在线, 0=离线）
      { id: 'PLC-STAT', name: '通信状态', value: 1, unit: '', min: 0, max: 1, normalMin: 1, normalMax: 1, trend: [], tau: 1 },
    ],
  },

  // ============= 7. 载荷传感器 — 实际 / 额定 / 百分比 =============
  {
    id: 'SENSOR-LOAD', name: '载荷传感器', type: 'instrument',
    x: 470, y: 250, width: 60, height: 40,
    status: 'normal', template: 'portcrane',
    parameters: [
      { id: 'LOAD-ACT', name: '实际载荷', value: 40, unit: 't', min: 0, max: 100, normalMin: 0, normalMax: 65, trend: [], tau: TAU_LOAD },
      { id: 'LOAD-RATED', name: '额定载荷', value: 65, unit: 't', min: 0, max: 100, normalMin: 65, normalMax: 65, trend: [], tau: 1 },
      { id: 'LOAD-PCT', name: '载荷百分比', value: 61.5, unit: '%', min: 0, max: 150, normalMin: 0, normalMax: 100, trend: [], tau: TAU_LOAD },
    ],
  },

  // ============= 8. 风速仪 — 风速 / 风向 =============
  {
    id: 'SENSOR-WIND', name: '风速仪', type: 'instrument',
    x: 700, y: 60, width: 80, height: 60,
    status: 'normal', template: 'portcrane',
    parameters: [
      { id: 'WIND-SPD', name: '风速', value: 12, unit: 'm/s', min: 0, max: 40, normalMin: 0, normalMax: 20, trend: [], tau: TAU_WIND },
      { id: 'WIND-DIR', name: '风向', value: 90, unit: '°', min: 0, max: 360, normalMin: 0, normalMax: 360, trend: [], tau: TAU_WIND },
    ],
  },

  // ============= 9. 供电系统 — 电压 / 电流 / 频率 =============
  {
    id: 'POWER', name: '供电系统', type: 'station',
    x: 100, y: 450, width: 100, height: 80,
    status: 'normal', template: 'portcrane',
    parameters: [
      { id: 'VOLT', name: '电压', value: 380, unit: 'V', min: 0, max: 500, normalMin: 361, normalMax: 399, trend: [], tau: TAU_POWER },
      { id: 'CURR', name: '电流', value: 120, unit: 'A', min: 0, max: 500, normalMin: 0, normalMax: 300, trend: [], tau: TAU_POWER },
      { id: 'FREQ', name: '频率', value: 50, unit: 'Hz', min: 45, max: 55, normalMin: 49.5, normalMax: 50.5, trend: [], tau: TAU_POWER },
    ],
  },

  // ============= 10. 轨道 — 偏差 / 磨损 =============
  {
    id: 'RAIL', name: '轨道', type: 'guidance',
    x: 100, y: 500, width: 700, height: 30,
    status: 'normal', template: 'portcrane',
    parameters: [
      { id: 'RAIL-DEV', name: '轨道偏差', value: 2, unit: 'mm', min: 0, max: 20, normalMin: 0, normalMax: 5, trend: [], tau: TAU_RAIL },
      { id: 'RAIL-WEAR', name: '磨损量', value: 3, unit: 'mm', min: 0, max: 20, normalMin: 0, normalMax: 10, trend: [], tau: TAU_RAIL },
    ],
  },
];

// ============= 主干管路（4 段）=============
// 岸桥作业链：供电 → PLC 控制 → 起升机构驱动吊具升降 → 大梁承载小车沿梁运行
export const portcranePipelines: Pipeline[] = [
  // 1. 电力供应：供电系统 → PLC 控制柜（黄色，电力传输）
  { id: 'PC-P-01', from: 'POWER', to: 'PLC', fromPoint: 'top', toPoint: 'bottom', medium: '电力供应', flowRate: 380, color: '#eab308' },
  // 2. 控制指令：PLC → 起升机构（橙色，控制信号）
  { id: 'PC-P-02', from: 'PLC', to: 'HOIST', fromPoint: 'right', toPoint: 'left', medium: '控制指令', flowRate: 1, color: '#f97316' },
  // 3. 起升钢丝绳：起升机构 → 吊具（红色，钢丝绳受力）
  { id: 'PC-P-03', from: 'HOIST', to: 'SPREADER', fromPoint: 'bottom', toPoint: 'top', medium: '起升钢丝绳', flowRate: 40, color: '#ef4444' },
  // 4. 大梁承载：大梁 → 小车（灰蓝色，结构承载）
  { id: 'PC-P-04', from: 'BOOM', to: 'TROLLEY', fromPoint: 'bottom', toPoint: 'top', medium: '结构承载', flowRate: 20, color: '#64748b' },
];
